import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { 
  Shield, 
  Upload, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Key, 
  FileKey, 
  Lock,
  Loader2,
  RefreshCw,
  Info,
  Calendar,
  FileCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CertificatesPage = () => {
  const [loading, setLoading] = useState(true);
  const [certStatus, setCertStatus] = useState(null);
  const [validation, setValidation] = useState(null);
  const [uploading, setUploading] = useState({ csd: false, fiel: false });
  const [deleting, setDeleting] = useState({ csd: false, fiel: false });
  
  // CSD form
  const [csdCerFile, setCsdCerFile] = useState(null);
  const [csdKeyFile, setCsdKeyFile] = useState(null);
  const [csdPassword, setCsdPassword] = useState("");
  
  // FIEL form
  const [fielCerFile, setFielCerFile] = useState(null);
  const [fielKeyFile, setFielKeyFile] = useState(null);
  const [fielPassword, setFielPassword] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, validationRes] = await Promise.all([
        axios.get(`${API}/certificates/status`, { headers }),
        axios.get(`${API}/certificates/validate`, { headers }).catch(() => null)
      ]);
      
      setCertStatus(statusRes.data);
      if (validationRes) {
        setValidation(validationRes.data);
      }
    } catch (error) {
      toast.error("Error al cargar estado de certificados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const uploadCSD = async () => {
    if (!csdCerFile || !csdKeyFile || !csdPassword) {
      toast.error("Complete todos los campos del CSD");
      return;
    }
    
    setUploading(prev => ({ ...prev, csd: true }));
    try {
      const token = localStorage.getItem("cia_token");
      const formData = new FormData();
      formData.append("cer_file", csdCerFile);
      formData.append("key_file", csdKeyFile);
      formData.append("password", csdPassword);
      
      await axios.post(`${API}/certificates/csd/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("CSD subido exitosamente");
      setCsdCerFile(null);
      setCsdKeyFile(null);
      setCsdPassword("");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir CSD");
    } finally {
      setUploading(prev => ({ ...prev, csd: false }));
    }
  };

  const uploadFIEL = async () => {
    if (!fielCerFile || !fielKeyFile || !fielPassword) {
      toast.error("Complete todos los campos de la FIEL");
      return;
    }
    
    setUploading(prev => ({ ...prev, fiel: true }));
    try {
      const token = localStorage.getItem("cia_token");
      const formData = new FormData();
      formData.append("cer_file", fielCerFile);
      formData.append("key_file", fielKeyFile);
      formData.append("password", fielPassword);
      
      await axios.post(`${API}/certificates/fiel/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("FIEL subida exitosamente");
      setFielCerFile(null);
      setFielKeyFile(null);
      setFielPassword("");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir FIEL");
    } finally {
      setUploading(prev => ({ ...prev, fiel: false }));
    }
  };

  const deleteCSD = async () => {
    if (!window.confirm("¿Está seguro de eliminar el CSD? No podrá timbrar facturas.")) {
      return;
    }
    
    setDeleting(prev => ({ ...prev, csd: true }));
    try {
      const token = localStorage.getItem("cia_token");
      await axios.delete(`${API}/certificates/csd`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("CSD eliminado");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar CSD");
    } finally {
      setDeleting(prev => ({ ...prev, csd: false }));
    }
  };

  const deleteFIEL = async () => {
    if (!window.confirm("¿Está seguro de eliminar la FIEL? No podrá cancelar facturas.")) {
      return;
    }
    
    setDeleting(prev => ({ ...prev, fiel: true }));
    try {
      const token = localStorage.getItem("cia_token");
      await axios.delete(`${API}/certificates/fiel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("FIEL eliminada");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar FIEL");
    } finally {
      setDeleting(prev => ({ ...prev, fiel: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("es-MX", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Certificados Digitales</h1>
          <p className="text-slate-500">Gestiona tu CSD y FIEL para facturación electrónica</p>
        </div>
        <Button variant="outline" onClick={fetchStatus} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Validation Status */}
      {validation && (
        <Alert variant={validation.valid ? "default" : "destructive"} className={validation.valid ? "border-green-200 bg-green-50" : ""}>
          {validation.valid ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertTitle>{validation.valid ? "Certificados Válidos" : "Atención Requerida"}</AlertTitle>
          <AlertDescription>
            {validation.valid ? (
              <span className="text-green-700">Todos los certificados están configurados correctamente.</span>
            ) : (
              <ul className="list-disc ml-4 mt-2 space-y-1">
                {validation.issues?.map((issue, idx) => (
                  <li key={idx}>{issue}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={certStatus?.has_csd ? "border-green-200" : "border-orange-200"}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              {certStatus?.has_csd ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <XCircle className="w-8 h-8 text-orange-500" />
              )}
              <div>
                <p className="font-medium">CSD</p>
                <p className="text-sm text-slate-500">
                  {certStatus?.has_csd ? "Configurado" : "No configurado"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={certStatus?.has_fiel ? "border-green-200" : "border-orange-200"}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              {certStatus?.has_fiel ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <XCircle className="w-8 h-8 text-orange-500" />
              )}
              <div>
                <p className="font-medium">FIEL</p>
                <p className="text-sm text-slate-500">
                  {certStatus?.has_fiel ? "Configurada" : "No configurada"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-[#5B9BD5]" />
              <div>
                <p className="font-medium">RFC</p>
                <p className="text-sm text-slate-500 font-mono">
                  {certStatus?.rfc || "N/A"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CSD Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Key className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>CSD - Certificado de Sello Digital</CardTitle>
                <CardDescription>
                  Requerido para timbrar facturas ante el SAT
                </CardDescription>
              </div>
            </div>
            {certStatus?.has_csd && (
              <Badge className="bg-green-500">Activo</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {certStatus?.has_csd ? (
            // CSD Info
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-500 text-xs">Número de Serie</Label>
                  <p className="font-mono text-sm">{certStatus.csd_serial || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Válido Desde</Label>
                  <p className="text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(certStatus.csd_valid_from)}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Válido Hasta</Label>
                  <p className="text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(certStatus.csd_valid_to)}
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={deleteCSD}
                  disabled={deleting.csd}
                  className="gap-2"
                >
                  {deleting.csd ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Eliminar CSD
                </Button>
              </div>
            </div>
          ) : (
            // CSD Upload Form
            <div className="space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>¿Cómo obtener el CSD?</AlertTitle>
                <AlertDescription>
                  El CSD se genera en el portal del SAT (sat.gob.mx) → Factura Electrónica → Certificados.
                  Necesitará su FIEL activa para generar el CSD.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="csd-cer">Archivo .cer (Certificado)</Label>
                  <Input
                    id="csd-cer"
                    type="file"
                    accept=".cer"
                    onChange={(e) => setCsdCerFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                  {csdCerFile && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <FileCheck className="w-3 h-3" />
                      {csdCerFile.name}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="csd-key">Archivo .key (Llave Privada)</Label>
                  <Input
                    id="csd-key"
                    type="file"
                    accept=".key"
                    onChange={(e) => setCsdKeyFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                  {csdKeyFile && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <FileCheck className="w-3 h-3" />
                      {csdKeyFile.name}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2 max-w-md">
                <Label htmlFor="csd-password">Contraseña del Certificado</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="csd-password"
                    type="password"
                    placeholder="Ingrese la contraseña"
                    value={csdPassword}
                    onChange={(e) => setCsdPassword(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Button 
                onClick={uploadCSD} 
                disabled={uploading.csd || !csdCerFile || !csdKeyFile || !csdPassword}
                className="gap-2 bg-[#5B9BD5] hover:bg-[#4a8bc4]"
              >
                {uploading.csd ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Subir CSD
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FIEL Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileKey className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <CardTitle>FIEL - Firma Electrónica Avanzada</CardTitle>
                <CardDescription>
                  Requerida para cancelar facturas y operaciones avanzadas
                </CardDescription>
              </div>
            </div>
            {certStatus?.has_fiel && (
              <Badge className="bg-green-500">Activa</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {certStatus?.has_fiel ? (
            // FIEL Info
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-500 text-xs">Número de Serie</Label>
                  <p className="font-mono text-sm">{certStatus.fiel_serial || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Válida Desde</Label>
                  <p className="text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(certStatus.fiel_valid_from)}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Válida Hasta</Label>
                  <p className="text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(certStatus.fiel_valid_to)}
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={deleteFIEL}
                  disabled={deleting.fiel}
                  className="gap-2"
                >
                  {deleting.fiel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Eliminar FIEL
                </Button>
              </div>
            </div>
          ) : (
            // FIEL Upload Form
            <div className="space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>¿Cómo obtener la FIEL?</AlertTitle>
                <AlertDescription>
                  La FIEL se tramita presencialmente en oficinas del SAT. Es su firma electrónica personal
                  y tiene validez de 4 años. No comparta su FIEL con terceros.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fiel-cer">Archivo .cer (Certificado)</Label>
                  <Input
                    id="fiel-cer"
                    type="file"
                    accept=".cer"
                    onChange={(e) => setFielCerFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                  {fielCerFile && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <FileCheck className="w-3 h-3" />
                      {fielCerFile.name}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="fiel-key">Archivo .key (Llave Privada)</Label>
                  <Input
                    id="fiel-key"
                    type="file"
                    accept=".key"
                    onChange={(e) => setFielKeyFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                  {fielKeyFile && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <FileCheck className="w-3 h-3" />
                      {fielKeyFile.name}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2 max-w-md">
                <Label htmlFor="fiel-password">Contraseña de la FIEL</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="fiel-password"
                    type="password"
                    placeholder="Ingrese la contraseña"
                    value={fielPassword}
                    onChange={(e) => setFielPassword(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Button 
                onClick={uploadFIEL} 
                disabled={uploading.fiel || !fielCerFile || !fielKeyFile || !fielPassword}
                className="gap-2 bg-purple-600 hover:bg-purple-700"
              >
                {uploading.fiel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Subir FIEL
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-500 mt-0.5" />
            <div className="space-y-2 text-sm text-slate-600">
              <p className="font-medium text-slate-700">¿Qué son el CSD y la FIEL?</p>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>CSD (Certificado de Sello Digital):</strong> Firma específica para facturación. Se genera desde el portal del SAT y tiene vigencia de 4 años.</li>
                <li><strong>FIEL (Firma Electrónica Avanzada):</strong> Firma personal que lo identifica ante el SAT. Se tramita presencialmente y es necesaria para cancelar facturas.</li>
              </ul>
              <p className="mt-3">
                <strong>Importante:</strong> Sus certificados se almacenan de forma segura y encriptada. 
                La contraseña nunca se comparte y solo se usa para firmar documentos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CertificatesPage;
